/**
 * The company list on `/about-us/family-of-companies/`: one block per company category
 * (Contentful `foCCompanyTypes`), in ascending `order`, each with a card per company linking to
 * `/about-us/family-of-companies/<slug>/` (`src/pages/about-us/family-of-companies/[slug].tsx`).
 *
 * - From `lg` up, each category is a heading, its description and a wrap of `./CompanyCard.tsx`
 *   hover cards. The chevron beside the heading collapses the cards with a `max-height`
 *   transition; the open height is `ceil(companies / 2) * 400px`, room for two cards per row.
 * - Below `lg`, each category is an accordion tab, open by default, of `./CompanyCardMobile.tsx`
 *   logo cards, each wrapped in a link.
 *
 * Within a category, both layouts sort the cards in place with a comparator that returns `-1`
 * when `a.companyName < b.companyName` and otherwise compares `a.name > b.name`. The preview
 * cards have no `name`, so it never returns `1`. With an inconsistent comparator the order is
 * up to the JavaScript engine's sort, so it isn't guaranteed to be alphabetical.
 *
 * Position: rendered only by `src/pages/about-us/family-of-companies/index.tsx`, which passes the
 * build-time result of
 * `src/contentful/family-of-companies/getContentfulFoCCompanyCategoriesAndCompanies.ts`; the
 * language is picked here in the browser.
 */
//ANIMATED STYLE FOC
import React from "react";
import { useTheme } from "@ryerson/frontend.theme";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { Icon } from "@ryerson/frontend.assets";
import { ContentSection, Flex, FlexItem } from "@ryerson/frontend.layout";
import { Media } from "@ryerson/frontend.next";
import { useApplication } from "@ryerson/frontend.application";
import { Typography } from "@ryerson/frontend.typography";
import { Accordion, AccordionTab } from "@ryerson/frontend.expansion";
import CompanyCard from "@components/FamilyOfCompanies/FamilyOfCompaniesRefactor/CompanyCard";
import CompanyCardMobile from "@components/FamilyOfCompanies/FamilyOfCompaniesRefactor/CompanyCardMobile";
import { Link } from "@ryerson/frontend.navigation";
import { Language } from "@ryerson/frontend.common";
import { ProcessedFoCCompanyCategories } from "@contentful/family-of-companies/types";

/**
 * An older category shape, with `specialtyDescription` nested as an object. Nothing imports it;
 * the component takes `ProcessedFoCCompanyCategories`
 * (`src/contentful/family-of-companies/types/index.ts`), whose `specialtyDescription` is a string.
 */
export type FoCCategoryContent = {
	contentful_id: string;
	slug: string;
	specialtyTitle: string;
	specialtyDescription: SpecialDescriptions;
	node_locale: "en-US" | "fr-CA" | "es-MX";
	companies: any[];
	order: number;
};

/** The nested description of {@link FoCCategoryContent}. Used only there. */
export type SpecialDescriptions = {
	specialtyDescription: string;
};

const HeaderBox = styled.div`
	width: 100%;
	box-sizing: border-box;
	position: relative;
`;

const CategorySection = styled.div`
	transition: max-height 0.35s, opacity 1s ease-in-out;
	overflow: hidden;
	margin-top: 10px;
`;

/**
 * One category from `lg` up: heading, collapse toggle, description and company cards. `category`
 * is a `ProcessedFoCCompanyCategories`, typed `any` here. Starts expanded.
 */
const FoCCompanyCardDesktop: React.FC<any> = ({ category }) => {
	const { theme } = useTheme();
	const [expanded, setExpanded] = React.useState(true);

	return (
		<>
			<div
				css={css`
					padding-bottom: 80px;
				`}
			>
				<HeaderBox>
					<Typography
						variant="h2"
						css={css`
							display: inline-block;
						`}
					>
						{category.specialtyTitle}
					</Typography>
					<div
						onClick={() => {
							setExpanded(!expanded);
						}}
						css={css`
							position: absolute;
							right: 0;
							top: 25%;
							transition: 1s linear all;
							border: 1px solid
								${expanded
									? theme.colors.primary.primaryBrand
									: theme.colors.primary.secondaryBrand};
							border-radius: 24px;
							width: 24px;
							height: 24px;
							text-align: center;
							line-height: 24px;
							transform: rotateX(${expanded ? "180deg" : "0deg"});
							transition: 0.35s transform linear, 0.35s border-color linear;
						`}
					>
						<Icon
							icon="chevron-up"
							size="xs"
							color={theme.colors.primary.secondaryBrand}
						/>
					</div>
				</HeaderBox>
				<Typography
					variant="p"
					css={css`
						color: ${theme.colors.secondary.darkBlue};
						margin-top: 60px;
					`}
				>
					{category.specialtyDescription}
				</Typography>

				<CategorySection
					css={css`
						max-height: ${expanded
							? Math.ceil(category.companies.length / 2) * 400 + "px"
							: "0px"};
					`}
				>
					<Flex
						direction="row"
						wrap="wrap"
						alignItems="center"
						justifyContent="flex-start"
						css={css`
							gap: 40px;
						`}
					>
						{category.companies
							.sort((a: any, b: any) => {
								if (a.companyName < b.companyName) {
									return -1;
								}
								if (a.name > b.name) {
									return 1;
								}
								return 0;
							})
							.map((companyContent: any, index: number) => {
								return (
									<FlexItem
										css={css`
											opacity: ${expanded ? 1 : 0};
											transition: 0.35s opacity linear;
										`}
										key={index}
									>
										<CompanyCard company={companyContent} />
									</FlexItem>
								);
							})}
					</Flex>
				</CategorySection>
			</div>
		</>
	);
};

/** Props of {@link FoCCategories}; also part of the overview page's props. */
export interface FoCCategoriesProps {
	/**
	 * Categories keyed by language, then by category slug, each with its companies' preview cards.
	 */
	foCCategoriesByLocale: Record<Language, Record<string, ProcessedFoCCompanyCategories>>;
}

/**
 * Renders every category for the current language. A missing `foCCategoriesByLocale` gives an
 * empty section; an object without an entry for the current language throws in `Object.entries`.
 */
const FoCCategories: React.FC<FoCCategoriesProps> = ({ foCCategoriesByLocale }) => {
	const {
		localization: { language },
	} = useApplication();

	let categoriesAndCompaniesContent = foCCategoriesByLocale
		? foCCategoriesByLocale[language]
		: [];

	return (
		<ContentSection type="secondary" vPadding="0">
			<Media greaterThanOrEqual="lg">
				<div
					css={css`
						height: 80px;
					`}
				></div>
				{Object.entries(categoriesAndCompaniesContent)
					.sort(([, a], [, b]) => a.order - b.order) // Sorting by 'order'
					.map(([categoryName, categoryContent]) => (
						<FoCCompanyCardDesktop category={categoryContent} key={categoryName} />
					))}
			</Media>
			<Media lessThan="lg">
				<Accordion multiple={true}>
					{Object.entries(categoriesAndCompaniesContent)
						.sort(([, a], [, b]) => a.order - b.order) // Sorting by 'order'
						.map(([categoryName, categoryContent]) => (
							<AccordionTab
								key={categoryName}
								header={categoryContent.specialtyTitle}
								circled={true}
								multiple={true}
								tabIndex={2}
								defaultOpen={true}
								maxHeight="15000px"
							>
								<Typography
									variant="p"
									size="md"
									css={css`
										margin-top: 30px;
									`}
								>
									{categoryContent.specialtyDescription}
								</Typography>
								<>
									{categoryContent.companies
										.sort((a: any, b: any) => {
											if (a.companyName < b.companyName) {
												return -1;
											}
											if (a.name > b.name) {
												return 1;
											}
											return 0;
										})
										.map((company: any) => (
											// Preview cards carry no `id`, so this key is
											// `undefined` for every card.
											<React.Fragment key={company.id}>
												<Link
													to={
														"/about-us/family-of-companies/" +
														company.slug
													}
												>
													<CompanyCardMobile content={company} />
												</Link>
											</React.Fragment>
										))}
								</>
							</AccordionTab>
						))}
				</Accordion>
			</Media>
		</ContentSection>
	);
};

export default FoCCategories;
